import {
  getAllBrandKeys,
  getAllBrandKeysWithBase,
  getBrandConfigForSubdomain,
  isBrandKey,
  type BrandConfig,
  type BrandKey,
  type BrandName,
} from "./config";

/**
 * Local storage key for brand debug override
 */
const BRAND_DEBUG_KEY = "brand-debug-override";

/**
 * Debug utilities for brand configuration
 *
 * Available in development mode via console as `brandDebug`:
 * - brandDebug.setBrandOverride('smartly') - Set brand override
 * - brandDebug.getBrandOverride() - Get current override
 * - brandDebug.clearBrandOverride() - Clear override
 * - brandDebug.getCurrentConfig() - Get current config
 * - brandDebug.listAvailableBrands() - List available brands
 */
export interface BrandDebugUtils {
  setBrandOverride: (brandKey: BrandKey) => void;
  getBrandOverride: () => BrandKey;
  clearBrandOverride: () => void;
  getCurrentConfig: () => BrandConfig;
  listAvailableBrands: () => readonly BrandName[];
  listAllBrandKeys: () => readonly (BrandName | "datapay")[];
}

/**
 * Sets a brand override in local storage with validation
 */
export function setBrandOverride(brandKey: BrandKey): void {
  try {
    if (brandKey && !isBrandKey(brandKey)) {
      console.error(`❌ Invalid brand key: ${brandKey}`);
      console.log(`Valid options: ${getAllBrandKeysWithBase().join(", ")}`);
      return;
    }

    if (brandKey) {
      localStorage.setItem(BRAND_DEBUG_KEY, brandKey);
      console.log(`🎨 Brand override set to: ${brandKey}`);
    } else {
      localStorage.removeItem(BRAND_DEBUG_KEY);
      console.log("🎨 Brand override cleared (set to null)");
    }
    console.log("Reload the page to see changes.");
  } catch (error) {
    console.error("Failed to set brand override:", error);
  }
}

/**
 * Gets the current brand override from local storage with validation
 */
export function getBrandOverride(): BrandKey {
  try {
    const stored = localStorage.getItem(BRAND_DEBUG_KEY);
    return isBrandKey(stored) ? stored : null;
  } catch (error) {
    console.error("Failed to get brand override:", error);
    return null;
  }
}

/**
 * Clears the brand override from local storage
 */
export function clearBrandOverride(): void {
  try {
    localStorage.removeItem(BRAND_DEBUG_KEY);
    console.log("🎨 Brand override cleared. Reload the page to see changes.");
  } catch (error) {
    console.error("Failed to clear brand override:", error);
  }
}

/**
 * Gets the current effective brand configuration
 */
export function getCurrentConfig(): BrandConfig {
  const override = getBrandOverride();
  return getBrandConfigForSubdomain(override);
}

/**
 * Lists all available brand override keys (excludes 'datapay' base)
 */
export const listAvailableBrands = getAllBrandKeys;

/**
 * Lists all possible brand keys including 'datapay'
 */
export const listAllBrandKeys = getAllBrandKeysWithBase;

/**
 * Installs debug utilities on the global window object
 * Call this in development to enable console debugging
 */
export function installBrandDebugUtils(): void {
  if (typeof window === "undefined") {
    return;
  }

  const debugUtils: BrandDebugUtils = {
    setBrandOverride,
    getBrandOverride,
    clearBrandOverride,
    getCurrentConfig,
    listAvailableBrands,
    listAllBrandKeys,
  };

  // Install on window for console access
  window.brandDebug = debugUtils;
}

// Extend the Window interface to include brandDebug for type safety
declare global {
  interface Window {
    brandDebug?: BrandDebugUtils;
  }
}
